import { memo, useCallback } from "react";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import { useField } from "react-final-form";
import { FilledTextArea } from "../../../../components/FilledTextArea";
import { formatBooleanConfigValue } from "../../utils/configFieldDisplay";

type Props = {
  name: string;
  label: string;
  value?: unknown;
  disabled?: boolean;
};

type EditableProps = {
  name: string;
  label: string;
};

type BooleanSelectValue = "true" | "false";

function toSelectValue(value: unknown): BooleanSelectValue {
  if (value === true) return "true";
  if (value === false) return "false";
  if (typeof value === "number") return value === 1 ? "true" : "false";
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true" || normalized === "yes") return "true";
  }
  return "false";
}

export const BooleanField = memo((props: Props) => {
  const { name, label, value, disabled } = props;
  if (disabled) {
    return (
      <FilledTextArea
        value={formatBooleanConfigValue(value)}
        name={name}
        label={label}
        inputProps={{ disabled: true }}
      />
    );
  }

  return <EditableBooleanField name={name} label={label} />;
});

const EditableBooleanField = memo((props: EditableProps) => {
  const { name, label } = props;
  const { input } = useField<boolean | undefined>(name, {
    subscription: { value: true },
  });
  const value = toSelectValue(input.value);
  const labelId = `${name}-bool-label`;

  const commitValue = useCallback(
    (nextValue: boolean) => {
      input.onChange(nextValue);
      input.onBlur();
    },
    [input],
  );

  const handleChange = useCallback(
    (event: SelectChangeEvent<BooleanSelectValue>) => {
      commitValue(event.target.value === "true");
    },
    [commitValue],
  );

  const commitSelectedOptionIfNeeded = useCallback(
    (nextValue: boolean) => {
      const nextSelectValue = nextValue ? "true" : "false";
      if (value === nextSelectValue && input.value !== nextValue) {
        // MUI does not fire Select.onChange when the already-selected option is
        // clicked. This keeps absent values visually on No while a deliberate
        // No click still writes false to the form.
        commitValue(nextValue);
      }
    },
    [commitValue, input.value, value],
  );

  return (
    <FormControl variant="filled" fullWidth>
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select<BooleanSelectValue>
        variant="filled"
        labelId={labelId}
        label={label}
        name={input.name}
        value={value}
        onBlur={input.onBlur}
        onFocus={input.onFocus}
        onChange={handleChange}
        inputProps={{
          "data-testid": `${name}-bool-select`,
        }}
      >
        <MenuItem
          value="true"
          onClick={() => commitSelectedOptionIfNeeded(true)}
        >
          Yes
        </MenuItem>
        <MenuItem
          value="false"
          onClick={() => commitSelectedOptionIfNeeded(false)}
        >
          No
        </MenuItem>
      </Select>
    </FormControl>
  );
});
